import type { CallConfiguration } from "@/schemas/agent.schema";

/**
 * Default messages for voice agent configurations by language
 */
export function getDefaultMessages(langCode: string) {
  const messages: Record<
    string,
    {
      initial_message: string;
      idle_message: string;
      inactivity_timeout: string;
      max_duration: string;
      presence_message?: string;
    }
  > = {
    pt: {
      initial_message: "Olá! Como posso ajudá-lo hoje?",
      idle_message: "Você está inativo há algum tempo. Posso ajudar com algo?",
      inactivity_timeout:
        "Devido ao tempo de inatividade, vou encerrar nossa conversa agora. Muito obrigado e até mais!",
      max_duration:
        "Nosso tempo de conversa chegou ao limite. Foi um prazer ajudá-lo. Até a próxima!",
      presence_message: "Você ainda está aí?",
    },
    en: {
      initial_message: "Hello! How can I help you today?",
      idle_message: "You've been inactive for a while. Can I help you with something?",
      inactivity_timeout:
        "Due to inactivity, I'll be ending our conversation now. Thank you very much and goodbye!",
      max_duration:
        "Our conversation time has reached its limit. It was a pleasure helping you. See you next time!",
      presence_message: "Are you still there?",
    },
    es: {
      initial_message: "¡Hola! ¿Cómo puedo ayudarte hoy?",
      idle_message: "Has estado inactivo por un tiempo. ¿Puedo ayudarte con algo?",
      inactivity_timeout:
        "Debido a la inactividad, voy a terminar nuestra conversación ahora. ¡Muchas gracias y hasta luego!",
      max_duration:
        "Nuestro tiempo de conversación ha llegado a su límite. Fue un placer ayudarte. ¡Hasta la próxima!",
      presence_message: "¿Sigues ahí?",
    },
    fr: {
      initial_message: "Bonjour ! Comment puis-je vous aider aujourd'hui ?",
      idle_message:
        "Vous avez été inactif pendant un moment. Puis-je vous aider avec quelque chose ?",
      inactivity_timeout:
        "En raison de l'inactivité, je vais terminer notre conversation maintenant. Merci beaucoup et au revoir !",
      max_duration:
        "Notre temps de conversation a atteint sa limite. Ce fut un plaisir de vous aider. À bientôt !",
      presence_message: "Êtes-vous toujours là ?",
    },
    de: {
      initial_message: "Hallo! Wie kann ich Ihnen heute helfen?",
      idle_message: "Sie waren eine Weile inaktiv. Kann ich Ihnen bei etwas helfen?",
      inactivity_timeout:
        "Aufgrund der Inaktivität werde ich unser Gespräch jetzt beenden. Vielen Dank und auf Wiedersehen!",
      max_duration:
        "Unsere Gesprächszeit hat ihr Limit erreicht. Es war mir eine Freude, Ihnen zu helfen. Bis zum nächsten Mal!",
      presence_message: "Sind Sie noch da?",
    },
    it: {
      initial_message: "Ciao! Come posso aiutarti oggi?",
      idle_message: "Sei stato inattivo per un po'. Posso aiutarti con qualcosa?",
      inactivity_timeout:
        "A causa dell'inattività, concluderò la nostra conversazione ora. Grazie mille e arrivederci!",
      max_duration:
        "Il nostro tempo di conversazione ha raggiunto il limite. È stato un piacere aiutarti. Alla prossima!",
      presence_message: "Ci sei ancora?",
    },
    ja: {
      initial_message: "こんにちは！今日はどのようにお手伝いできますか？",
      idle_message: "しばらく操作がありませんでした。何かお手伝いできることはありますか？",
      inactivity_timeout:
        "操作がないため、会話を終了させていただきます。ありがとうございました。さようなら！",
      max_duration:
        "会話時間が上限に達しました。お手伝いできて光栄でした。また次回お会いしましょう！",
      presence_message: "まだいらっしゃいますか？",
    },
    zh: {
      initial_message: "您好！今天我可以为您做些什么？",
      idle_message: "您已经有一段时间没有活动了。我可以帮您做什么吗？",
      inactivity_timeout: "由于不活跃，我现在要结束我们的对话。非常感谢，再见！",
      max_duration: "我们的对话时间已达到限制。很高兴能为您提供帮助。下次再见！",
      presence_message: "您还在吗？",
    },
    ru: {
      initial_message: "Привет! Чем могу помочь вам сегодня?",
      idle_message: "Вы были неактивны некоторое время. Могу ли я чем-то помочь?",
      inactivity_timeout:
        "Из-за неактивности я заканчиваю наш разговор. Большое спасибо и до свидания!",
      max_duration: "Время нашего разговора достигло предела. Было приятно помочь вам. До встречи!",
      presence_message: "Вы еще здесь?",
    },
    ar: {
      initial_message: "مرحباً! كيف يمكنني مساعدتك اليوم؟",
      idle_message: "لقد كنت غير نشط لفترة. هل يمكنني مساعدتك في شيء؟",
      inactivity_timeout: "بسبب عدم النشاط، سأنهي محادثتنا الآن. شكراً جزيلاً ووداعاً!",
      max_duration: "وقت محادثتنا وصل إلى حده الأقصى. كان من دواعي سروري مساعدتك. إلى اللقاء!",
      presence_message: "هل ما زلت هناك؟",
    },
    hi: {
      initial_message: "नमस्ते! आज मैं आपकी कैसे मदद कर सकता हूँ?",
      idle_message: "आप कुछ समय से निष्क्रिय हैं। क्या मैं किसी चीज़ में आपकी मदद कर सकता हूँ?",
      inactivity_timeout:
        "निष्क्रियता के कारण, मैं अब हमारी बातचीत समाप्त कर रहा हूँ। बहुत धन्यवाद और अलविदा!",
      max_duration:
        "हमारे बातचीत का समय अपनी सीमा तक पहुँच गया है। आपकी मदद करके खुशी हुई। फिर मिलेंगे!",
      presence_message: "क्या आप अभी भी वहाँ हैं?",
    },
    ko: {
      initial_message: "안녕하세요! 오늘 어떻게 도와드릴까요?",
      idle_message: "잠시 동안 활동이 없으셨네요. 도움이 필요한 것이 있나요?",
      inactivity_timeout: "활동이 없어서 대화를 종료하겠습니다. 감사합니다. 안녕히 가세요!",
      max_duration:
        "대화 시간이 한계에 도달했습니다. 도움을 드릴 수 있어서 기뻤습니다. 다음에 또 만나요!",
      presence_message: "아직 계시나요?",
    },
  };

  // Return messages for the language if available, otherwise return default English
  return (
    messages[langCode] || {
      initial_message: "",
      idle_message: "",
      inactivity_timeout: "",
      max_duration: "",
      presence_message: "Are you still there?",
    }
  );
}

/**
 * Create default call configuration with language-specific messages
 * @param lang - Language code for default messages
 * @returns Complete CallConfiguration with defaults (includes all fields now)
 */
export function createDefaultCallConfiguration(lang: string): CallConfiguration {
  const defaultMessages = getDefaultMessages(lang);

  return {
    initial_message: defaultMessages.initial_message, // LEGACY - kept for backward compatibility
    additional_instructions: undefined,
    variables: undefined,
    // NEW: Default greeting configuration (generated strategy recommended)
    greeting: {
      type: "generated",
      generation_context: undefined,
      include_thread_summary: true,
    },
    inactivity_timeout: {
      enabled: false,
      time_in_seconds: 60,
      message: defaultMessages.inactivity_timeout,
      presence_attempts: 0 as number,
      presence_message: defaultMessages.presence_message,
    },
    max_duration: {
      time_in_minutes: 30,
      message: defaultMessages.max_duration,
    },
    ambient_sound: {
      enabled: false,
      audio: "ambient_1",
      volume: 0.5,
    },
    thinking_sound: {
      enabled: true,
      audio: "thinking_1",
      volume: 0.3,
    },
    tools: undefined,
  };
}

/**
 * Normalize a (possibly partial) call configuration coming from the API.
 *
 * Agents created before some of these sub-objects existed (greeting,
 * inactivity_timeout, max_duration, ambient_sound, thinking_sound) arrive with a
 * partial call_configuration and crash the voice components that assume they
 * are always present. This backfills only the missing sub-objects with the
 * language-specific defaults, without touching any field that is already set.
 *
 * Preserves the legacy initial_message → greeting migration: when greeting is
 * missing but a legacy initial_message exists, a contextual greeting is built
 * from it instead of the "generated" default.
 * @param config - The call configuration to normalize, possibly undefined/null or partial
 * @param lang - Language code used to build default messages when needed
 * @returns The normalized CallConfiguration (same reference when config is not nullish)
 */
export function normalizeCallConfiguration(
  config: CallConfiguration | undefined | null,
  lang: string
): CallConfiguration {
  if (!config) {
    return createDefaultCallConfiguration(lang);
  }

  const defaults = createDefaultCallConfiguration(lang);

  // MIGRATION: legacy configs have initial_message but no greeting; convert it
  // to a contextual greeting instead of applying the "generated" default.
  if (!config.greeting && config.initial_message) {
    config.greeting = {
      type: "contextual",
      fixed_message: config.initial_message,
      generation_context: undefined,
      include_thread_summary: true,
      allow_interruptions: false,
    };
  }

  config.greeting ??= defaults.greeting;
  config.inactivity_timeout ??= defaults.inactivity_timeout;
  config.max_duration ??= defaults.max_duration;
  config.ambient_sound ??= defaults.ambient_sound;
  config.thinking_sound = config.thinking_sound
    ? { ...defaults.thinking_sound, ...config.thinking_sound }
    : defaults.thinking_sound;

  return config;
}

/**
 * Create default voice agent configuration with language-specific messages
 * @param lang - Language code for default messages
 * @returns Complete VoiceAgentConfiguration with defaults
 * @deprecated Use createDefaultCallConfiguration instead. VoiceAgentConfiguration is now an alias to CallConfiguration.
 * This function will be removed in a future version.
 */
export function createDefaultVoiceConfiguration(lang: string): CallConfiguration {
  return createDefaultCallConfiguration(lang);
}
